import React, { useContext, useEffect, useReducer, useMemo } from 'react'
import { getSession } from '@fs/zion-session'
import { useUser } from '@fs/zion-user'
import zionDebug from '@fs/zion-debug'
import { describeError, doFetchGet, getXRequestId, tempWindowFSRemoteLog } from '../apiService/apiService'
import { getPlacePermissions } from '../apiService/placePermissionService'
import { hasAnyPermissionForFeature } from '../apiService/placePermissionHelpers'
import searchReducer, { DEFAULT_SEARCH_STATE } from '../Search/searchReducer'
import type { SearchState, SearchAction } from '../Search/searchReducer'

const debug = zionDebug('standards:places:PlacesAppContext')

type PlaceServiceHealth = {
  canRead: boolean
  canWrite: boolean
}

type UIState = {
  resultsAreHidden: boolean
  detailsAreHidden: boolean
  resultsIsOffScreen: boolean
}

type HistoryState = {
  location: {
    search: string
  }
}

type PlacesAppState = {
  permissions: Record<string, unknown>
  editMode: boolean
  keepAlive: number | null
  sendkeepAlive: number | null
  history: HistoryState
  ui: UIState
  authComplete?: boolean
  health?: PlaceServiceHealth
  isDirty?: boolean
}

type PlacesAppAction =
  | { type: 'permissions'; permissions: Record<string, unknown> }
  | { type: 'editMode'; editMode: boolean }
  | { type: 'authComplete'; authComplete: boolean }
  | { type: 'health'; health: PlaceServiceHealth }
  | { type: 'isDirty'; isDirty: boolean }
  | { type: 'SET_HISTORY'; history: HistoryState }
  | { type: 'SET_UI_STATE'; uiState: Partial<UIState> }

type ContextValue = PlacesAppState & {
  searchState: SearchState
  dispatchSearch: React.Dispatch<SearchAction>
  setResultsAreHidden: (value: boolean) => void
  setDetailsAreHidden: (value: boolean) => void
  setResultsIsOffScreen: (value: boolean) => void
}

const PlacesAppStateContext = React.createContext<ContextValue | undefined>(undefined)
const PlacesAppDispatchContext = React.createContext<React.Dispatch<PlacesAppAction> | undefined>(undefined)

function placesAppReducer(state: PlacesAppState, action: PlacesAppAction): PlacesAppState {
  switch (action.type) {
    case 'permissions': {
      return { ...state, permissions: action.permissions }
    }
    case 'editMode': {
      return { ...state, editMode: !!action.editMode }
    }
    case 'authComplete': {
      return { ...state, authComplete: action.authComplete }
    }
    // See if we can have the backend use the official maintenance window
    case 'health': {
      return { ...state, health: action.health }
    }
    case 'isDirty': {
      return { ...state, isDirty: action.isDirty }
    }
    case 'SET_HISTORY': {
      return { ...state, history: action.history }
    }
    case 'SET_UI_STATE': {
      return {
        ...state,
        ui: {
          ...state.ui,
          ...action.uiState,
        },
      }
    }

    default: {
      throw new Error(`Unhandled action type: ${(action as { type: string }).type}`)
    }
  }
}

async function getHealth(): Promise<PlaceServiceHealth> {
  const url = `/service/standards/place/ws-write/healthcheck/status`

  let resp
  let placeServiceHealth: PlaceServiceHealth = { canRead: false, canWrite: false }

  try {
    resp = await doFetchGet(url, {
      headers: { accept: 'application/json', 'Content-Type': 'application/json', 'X-Request-ID': getXRequestId() },
    })
    const canRead = resp?.data?.solrReadConnected && resp?.data?.dbReadReady
    const canWrite = resp?.data?.solrWriteConnected && resp?.data?.solrWriteReady && resp?.data?.dbWriteReady
    placeServiceHealth = { canRead, canWrite }
  } catch (err) {
    debug('Error while performing standards/place/ws-write/healthcheck/status:', err)
    tempWindowFSRemoteLog(
      `Error while performing standards/place/ws-write/healthcheck/status: ${describeError(err)}`,
      1
    )
  }

  return placeServiceHealth
}

let SendKeepAliveTimer: number | null = null
let KeepAliveTimer: number | null = null

function keepAliveSession(): void {
  const SEND_KEEPALIVE_INTERVAL = 300000 // 5 minutes; stops after EXTENDED_SESSION_TIMEOUT
  SendKeepAliveTimer = window.setInterval(() => {
    // checking if session is alive also keeps it alive :)
    // User.getSession(true) - this will call get session and ignore the cache.
    const isAlive = !!getSession(true)
    if (!isAlive) {
      debug('#### Session is DEAD ###')
      window.location.assign(`/auth/familysearch/login?returnurl=${encodeURIComponent(window.location.href)}`)
    }
  }, SEND_KEEPALIVE_INTERVAL)

  const EXTENDED_SESSION_TIMEOUT = 3600000 // 1 hour in milliseconds
  KeepAliveTimer = window.setTimeout(() => {
    KeepAliveTimer = null
    window.clearInterval(SendKeepAliveTimer!)
    SendKeepAliveTimer = null
  }, EXTENDED_SESSION_TIMEOUT)
}

type PlacesAppProviderProps = Readonly<{
  children: React.ReactNode
}>

function PlacesAppProvider({ children }: PlacesAppProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(placesAppReducer, {
    permissions: {},
    editMode: false,
    keepAlive: null,
    sendkeepAlive: null,
    history: { location: { search: '' } },
    ui: {
      resultsAreHidden: false,
      detailsAreHidden: false,
      resultsIsOffScreen: false,
    },
  })

  // Add search state management
  const [searchState, dispatchSearch] = useReducer(searchReducer, DEFAULT_SEARCH_STATE)

  async function fetchPermissions(): Promise<Record<string, unknown>> {
    const permissions = await getPlacePermissions()
    dispatch({ type: 'permissions', permissions })
    return permissions
  }

  async function getPlaceServiceHealth(): Promise<PlaceServiceHealth> {
    const health = await getHealth()
    dispatch({ type: 'health', health })
    return health
  }

  const signedIn = Boolean(useUser()?.signedIn)

  useEffect(() => {
    getPlaceServiceHealth()
  }, [])

  // CAS answers a signed-out visitor with 400, so skip the call; {} means no permissions
  useEffect(() => {
    if (signedIn) fetchPermissions()
    else dispatch({ type: 'permissions', permissions: {} })
  }, [signedIn])

  const { permissions, health } = state

  // console.log('permissions:', permissions)
  useEffect(() => {
    if (!permissions || health === undefined) return
    const editMode = hasAnyPermissionForFeature(permissions as never, 'CAN_EDIT', health)
    dispatch({ type: 'editMode', editMode })
    dispatch({ type: 'authComplete', authComplete: true })
  }, [permissions, health])

  const { editMode } = state
  useEffect(() => {
    if (editMode) {
      if (!KeepAliveTimer) {
        keepAliveSession()
      }
    }
  }, [editMode])

  // Enhanced context value with useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      ...state,
      searchState,
      dispatchSearch,
      // UI state setters
      setResultsAreHidden: (value: boolean) =>
        dispatch({
          type: 'SET_UI_STATE',
          uiState: { resultsAreHidden: value },
        }),
      setDetailsAreHidden: (value: boolean) =>
        dispatch({
          type: 'SET_UI_STATE',
          uiState: { detailsAreHidden: value },
        }),
      setResultsIsOffScreen: (value: boolean) =>
        dispatch({
          type: 'SET_UI_STATE',
          uiState: { resultsIsOffScreen: value },
        }),
    }),
    [state, searchState, dispatchSearch]
  )

  return (
    <PlacesAppStateContext.Provider value={contextValue}>
      <PlacesAppDispatchContext.Provider value={dispatch}>{children}</PlacesAppDispatchContext.Provider>
    </PlacesAppStateContext.Provider>
  )
}

function usePlacesAppState(): ContextValue {
  const context = useContext(PlacesAppStateContext)
  if (context === undefined) {
    throw new Error('usePlacesAppState must be used within a PlacesAppProvider')
  }
  return context
}

// For content that can also render outside the app (e.g. the map popup in isolated tests)
function useOptionalPlacesAppState(): ContextValue | undefined {
  return useContext(PlacesAppStateContext)
}

function usePlacesAppDispatch(): React.Dispatch<PlacesAppAction> {
  const context = useContext(PlacesAppDispatchContext)
  if (context === undefined) {
    throw new Error('usePlacesAppDispatch must be used within a PlacesAppProvider')
  }
  return context
}

export { PlacesAppProvider, usePlacesAppState, useOptionalPlacesAppState, usePlacesAppDispatch }
